import { CheckCircle2, Pause, Play, XCircle } from "lucide-react";
import * as React from "react";

import { type TestRunResponse, workflowsApi } from "@/api/workflows";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useWorkflowDesignerStore } from "@/stores/workflowDesigner";
import { useUIStore } from "@/stores/ui";

/**
 * TestRun — sandbox chạy workflow với dry_run mock LLM/tool.
 *
 * User nhập initial_data JSON, nhận trace per-node + final state.
 */
export function TestRun(): JSX.Element {
  const code = useWorkflowDesignerStore((s) => s.workflowCode);
  const isDirty = useWorkflowDesignerStore((s) => s.isDirty);
  const versionInfo = useWorkflowDesignerStore((s) => s.versionInfo);
  const pushToast = useUIStore((s) => s.pushToast);

  const [initialData, setInitialData] = React.useState("{}");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<TestRunResponse | null>(null);

  const handleRun = async (): Promise<void> => {
    if (!code) return;
    if (isDirty) {
      pushToast({
        variant: "warning",
        title: "Save Draft trước",
        description: "Test run dùng version đã save trong DB.",
      });
      return;
    }
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(initialData);
    } catch {
      pushToast({ variant: "destructive", title: "initial_data không phải JSON" });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await workflowsApi.testRun(code, {
        initial_data: parsed,
        version: versionInfo?.version ?? null,
        max_steps: 50,
      });
      setResult(res);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Test run lỗi",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 space-y-3 bg-muted/30 min-h-full">
      <div>
        <label className="block text-xs font-medium mb-1">
          Initial state.data (JSON)
        </label>
        <Textarea
          value={initialData}
          onChange={(e) => setInitialData(e.target.value)}
          rows={4}
          className="text-xs font-mono"
          placeholder='{"amount": 100000000, "department": "Sales"}'
        />
      </div>
      <Button
        onClick={() => void handleRun()}
        disabled={busy || !code}
        className="w-full"
      >
        <Play className="size-4 mr-1" />
        {busy ? "Đang chạy..." : "Test Run (dry-run, mock LLM/tool)"}
      </Button>

      {result ? (
        <div className="rounded-md border bg-background p-3 space-y-2">
          <StatusBanner status={result.status} error={result.error} />
          {result.current_node ? (
            <div className="text-xs">
              <span className="text-muted-foreground">Current node:</span>{" "}
              <code className="bg-muted px-1 rounded">{result.current_node}</code>
            </div>
          ) : null}

          <div>
            <div className="text-[11px] uppercase font-semibold text-muted-foreground mb-1">
              Trace
            </div>
            <ol className="text-xs space-y-1">
              {result.history.map((h, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded border bg-muted/30 px-2 py-1"
                >
                  <span className="text-muted-foreground tabular-nums">
                    {i + 1}.
                  </span>
                  <code className="font-mono">{h.node_id}</code>
                  <Badge variant="outline" className="text-[10px]">
                    {h.type}
                  </Badge>
                  {h.branch_label ? (
                    <Badge variant="secondary" className="text-[10px]">
                      → {h.branch_label}
                    </Badge>
                  ) : null}
                  {h.error ? (
                    <span className="text-rose-700 text-[11px]">
                      {h.error}
                    </span>
                  ) : null}
                  {h.logs && h.logs.length ? (
                    <span className="text-muted-foreground text-[10px] flex-1 truncate">
                      {h.logs.join(" · ")}
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          </div>

          <details>
            <summary className="text-xs cursor-pointer text-muted-foreground">
              Final state.data ({Object.keys(result.final_data).length} keys)
            </summary>
            <pre className="text-[11px] bg-muted rounded p-2 mt-1 overflow-x-auto">
              {JSON.stringify(result.final_data, null, 2)}
            </pre>
          </details>

          {result.pause_payload ? (
            <details>
              <summary className="text-xs cursor-pointer text-muted-foreground">
                Pause payload (HITL)
              </summary>
              <pre className="text-[11px] bg-muted rounded p-2 mt-1 overflow-x-auto">
                {JSON.stringify(result.pause_payload, null, 2)}
              </pre>
            </details>
          ) : null}

          {result.emitted_widgets.length > 0 ? (
            <details>
              <summary className="text-xs cursor-pointer text-muted-foreground">
                Emitted widgets ({result.emitted_widgets.length})
              </summary>
              <pre className="text-[11px] bg-muted rounded p-2 mt-1 overflow-x-auto">
                {JSON.stringify(result.emitted_widgets, null, 2)}
              </pre>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function StatusBanner({
  status,
  error,
}: {
  status: string;
  error: string | null;
}): JSX.Element {
  if (status === "completed") {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-700">
        <CheckCircle2 className="size-4" /> Hoàn thành
      </div>
    );
  }
  if (status === "paused") {
    return (
      <div className="flex items-center gap-2 text-sm text-amber-700">
        <Pause className="size-4" /> Paused (chờ user input)
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="flex items-center gap-2 text-sm text-rose-700">
        <XCircle className="size-4" />
        {error || "Failed"}
      </div>
    );
  }
  return (
    <Badge variant="outline" className="text-xs">
      {status}
    </Badge>
  );
}
